import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FOCAS_MODES, DEFAULT_TLM, resolveAddressing, toolKeysFromState, tlmValue, saveFocasConfig,
  getToolChange, triggerToolChange, setToolChangeMode,
  CAP_META, capsFromConfig,
} from '../../../utils/focasAddressing';
import { toolWearLevel } from '../hmiTools';
import { CrtSection, CrtButton, CrtNumField, CrtToggle, CrtBadge } from './crtKit';

const MODE_LABEL = { macros: 'USER MACROS', pcodes: 'P-CODE MACROS', pmc: 'PMC REGISTERS' };
const MODE_HELP = {
  macros: 'Tool-life on user macro variables (read via cnc_rdmacror2). Default onboarding path.',
  pcodes: 'Tool-life on P-code (executor) macros — same wire read, high number range.',
  pmc: 'Tool-life on PMC registers (R-area). Read via cnc_rdpmcrng.',
};

const tlmFromConfig = (fc) => ({ ...DEFAULT_TLM, ...((fc && fc.tlm) || {}), map: {} });

/**
 * FANUC SYSTEM page — FOCAS tool-life addressing + machine capabilities +
 * operator/scenario controls. This is the phosphor-CRT successor to the old
 * gray FocasPanel; it reads the panel's live socket `state` rather than opening
 * its own, and edits/persists via the same focasAddressing helpers.
 */
export default function SystemScreen({ machine, state = {} }) {
  const [tlm, setTlm] = useState(() => tlmFromConfig(machine.focasConfig));
  const [caps, setCaps] = useState(() => capsFromConfig(machine.focasConfig));
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(false);
  const [error, setError] = useState(null);
  const [tc, setTc] = useState({ mode: 'auto', downtimeSec: 40, phase: null });
  const [tcBusy, setTcBusy] = useState(false);

  const refreshTc = useCallback(async () => {
    try { setTc(await getToolChange(machine.name)); } catch { /* engine may not have the machine */ }
  }, [machine.name]);

  useEffect(() => { refreshTc(); }, [refreshTc]);

  const toolKeys = useMemo(() => toolKeysFromState(state), [state]);
  const addressing = useMemo(() => resolveAddressing({ tlm }, toolKeys), [tlm, toolKeys]);

  const setField = (k, v) => { setTlm((t) => ({ ...t, [k]: v })); setSavedAt(false); };

  const onSave = useCallback(async () => {
    setSaving(true); setError(null);
    try { await saveFocasConfig(machine.name, { tlm, capabilities: caps }); setSavedAt(true); }
    catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  }, [machine.name, tlm, caps]);

  const onToggleCap = useCallback(async (key, value) => {
    const next = { ...caps, [key]: value };
    setCaps(next); setSaving(true); setError(null);
    try { await saveFocasConfig(machine.name, { tlm, capabilities: next }); setSavedAt(true); }
    catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  }, [caps, tlm, machine.name]);

  const onTrigger = useCallback(async (positions) => {
    setTcBusy(true); setError(null);
    try {
      const r = await triggerToolChange(machine.name, positions);
      if (r && r.success === false) setError(r.error);
      await refreshTc();
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setTcBusy(false); }
  }, [machine.name, refreshTc]);

  const onSetMode = useCallback(async (patch) => {
    setTcBusy(true); setError(null);
    try { await setToolChangeMode(machine.name, patch); await refreshTc(); }
    catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setTcBusy(false); }
  }, [machine.name, refreshTc]);

  const holding = tc.phase === 'tool_change';
  const tklist = addressing ? Object.keys(addressing).sort() : [];

  // Tool-life rows — kept out of the main return for readability.
  const toolRows = () =>
    tklist.map((tk) => {
      const count = tlmValue(state, tk, 'COUNT') ?? 0;
      const preset = tlmValue(state, tk, 'PRESET') ?? 0;
      const pct = preset > 0 ? Math.min(100, Math.round((count / preset) * 100)) : 0;
      const a = addressing[tk];
      return (
        <tr key={tk} style={{ borderTop: '1px solid color-mix(in srgb, var(--fx-dim) 25%, transparent)' }}>
          <td className="px-3 py-2 font-mono fanuc-value">{tk}</td>
          <td className="px-3 py-2 text-right font-mono fanuc-value tabular-nums">{count}</td>
          <td className="px-3 py-2 text-right font-mono fanuc-label tabular-nums">{preset}</td>
          <td className="px-3 py-2">
            <div className="fanuc-tlbar h-2 rounded overflow-hidden">
              <span className={`tl-${toolWearLevel(pct)}`} style={{ width: `${pct}%` }} />
            </div>
          </td>
          <td className="px-3 py-2 font-mono fanuc-value">{a.count.label}</td>
          <td className="px-3 py-2 font-mono fanuc-value">{a.preset.label}</td>
          <td className="px-3 py-2 text-right">
            <CrtButton tone="warn" disabled={tcBusy} onClick={() => onTrigger([tk])}
              title={`Simulate operator changing ${tk} → counter drops to 0 (${pct}% used life)`}
              className="px-2 py-1">
              CHANGE
            </CrtButton>
          </td>
        </tr>
      );
    });

  return (
    <div className="text-sm">
      {/* Addressing mode */}
      <CrtSection title="Tool-life addressing">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {FOCAS_MODES.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setField('mode', m)}
              className={`crt-btn ${tlm.mode === m ? 'is-on' : ''} p-2.5 text-left`}
            >
              <div className="text-xs font-bold tracking-wider">{MODE_LABEL[m]}</div>
              <div className={`text-[11px] mt-1 leading-snug ${tlm.mode === m ? '' : 'fanuc-label'}`}>
                {MODE_HELP[m]}
              </div>
            </button>
          ))}
        </div>
      </CrtSection>

      {/* Base / address inputs */}
      <div className="flex flex-wrap gap-4 mb-3">
        {tlm.mode === 'pmc' ? (
          <>
            <CrtNumField label="Addr type" value={tlm.addrType} onChange={(v) => setField('addrType', v)} />
            <CrtNumField label="Count addr" value={tlm.countAddr} onChange={(v) => setField('countAddr', v)} />
            <CrtNumField label="Preset addr" value={tlm.presetAddr} onChange={(v) => setField('presetAddr', v)} />
            <CrtNumField label="Width (bytes)" value={tlm.width} onChange={(v) => setField('width', v)} />
          </>
        ) : (
          <>
            <CrtNumField label="Count base" value={tlm.countBase} onChange={(v) => setField('countBase', v)} />
            <CrtNumField label="Preset base" value={tlm.presetBase} onChange={(v) => setField('presetBase', v)} />
          </>
        )}
      </div>

      {/* Resolved tool-life table */}
      <CrtSection title="Resolved addresses">
        <div className="fanuc-cell overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="fanuc-label text-[10px] uppercase tracking-wide">
                <th className="text-left px-3 py-2">Tool</th>
                <th className="text-right px-3 py-2">Count</th>
                <th className="text-right px-3 py-2">Preset</th>
                <th className="text-left px-3 py-2 w-32">Life</th>
                <th className="text-left px-3 py-2">Count addr</th>
                <th className="text-left px-3 py-2">Preset addr</th>
                <th className="text-right px-3 py-2">Change</th>
              </tr>
            </thead>
            <tbody>
              {tklist.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-4 text-center fanuc-label">
                    No tool-life reported yet (no running tooled operation).
                  </td>
                </tr>
              )}
              {toolRows()}
            </tbody>
          </table>
        </div>
      </CrtSection>

      {/* Operator / scenario */}
      <CrtSection
        title="Operator / scenario"
        right={holding ? (
          <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded"
            style={{ color: '#fde68a', background: 'rgba(120,80,0,0.4)' }}>
            HOLDING — AWAITING TOOL CHANGE
          </span>
        ) : null}
      >
        <div className="fanuc-cell p-3 flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1">
            <span className="fanuc-label text-[10px] uppercase tracking-wide">Change mode</span>
            <div className="flex gap-1">
              {['auto', 'manual'].map((m) => (
                <CrtButton key={m} active={tc.mode === m} disabled={tcBusy} onClick={() => onSetMode({ mode: m })}>
                  {m === 'auto' ? 'AUTO' : 'MANUAL (HOLD)'}
                </CrtButton>
              ))}
            </div>
          </div>
          <CrtNumField
            label="Peak hold (s)" value={tc.downtimeSec} width="w-24"
            onChange={(v) => setTc((t) => ({ ...t, downtimeSec: v }))}
            onBlur={() => onSetMode({ downtimeSec: Number(tc.downtimeSec) })}
          />
          <CrtButton tone="warn" disabled={tcBusy} onClick={() => onTrigger(null)}>CHANGE ALL DUE</CrtButton>
        </div>
        <p className="fanuc-label text-[11px] mt-2 leading-snug">
          MANUAL holds the machine at preset until you press CHANGE (the operator swap). PEAK HOLD ≥ ~2× the
          adapter poll (≈40s) so &quot;at life&quot; classifies cleanly.
        </p>
      </CrtSection>

      {/* Capabilities */}
      <CrtSection title="Machine capabilities">
        <div className="fanuc-cell p-3">
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            {['tool_life', 'tool_groups'].map((k) => (
              <CrtToggle key={k} label={CAP_META[k].label} help={CAP_META[k].help}
                checked={!!caps[k]} disabled={saving} onChange={(v) => onToggleCap(k, v)} />
            ))}
          </div>
          <div className="mt-3 pt-3 flex flex-wrap items-center gap-2"
            style={{ borderTop: '1px solid color-mix(in srgb, var(--fx-dim) 35%, transparent)' }}>
            <span className="fanuc-label text-[11px]">Sim profile (applies on restart):</span>
            <CrtBadge label={`${caps.paths} path${caps.paths > 1 ? 's' : ''}`} on />
            {['program_upload', 'macro_r2', 'pmc', 'identity_scrub'].map((k) => (
              <CrtBadge key={k} label={CAP_META[k].label} on={!!caps[k]} />
            ))}
          </div>
        </div>
      </CrtSection>

      {/* Actions */}
      <div className="flex items-center gap-3 flex-wrap">
        <CrtButton active disabled={saving} onClick={onSave} className="px-4 py-2">
          {saving ? 'SAVING…' : 'SAVE ADDRESSING'}
        </CrtButton>
        <CrtButton onClick={() => { setTlm(tlmFromConfig(machine.focasConfig)); setSavedAt(false); setError(null); }} className="px-4 py-2">
          RESET
        </CrtButton>
        {savedAt && <span className="fanuc-value text-xs">Saved — adapter picks it up within ~1s.</span>}
        {error && <span className="text-xs" style={{ color: '#fca5a5' }}>{error}</span>}
      </div>
    </div>
  );
}
